/**
 * Step 1: Plan Basics
 * Captures plan name, type, and as-of date
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Step1FormData } from '../../types/wizard';
import { wizardFormKeyDown } from './wizardFormKeyDown';
import { TourButton } from '../TourGuide';
import { WIZARD_STEP1_TOUR } from '../TourGuide/tourSteps';
import './Step1PlanBasics.css';

// Validation schema
const Step1Schema = z.object({
  planName: z.string().min(1, 'Plan name is required').max(100, 'Plan name must be less than 100 characters'),
  planType: z.enum(['SINGLE', 'JOINT'], { required_error: 'Please select a plan type' }),
  planAsOfDate: z.string().min(1, 'Plan date is required'),
});

interface Step1Props {
  initialData?: Partial<Step1FormData>;
  onNext: (data: Step1FormData) => void;
  onSave: (data: Step1FormData) => void;
  lastSaved?: string;
}

function Step1PlanBasics({ initialData, onNext, onSave, lastSaved }: Readonly<Step1Props>) {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isDirty },
  } = useForm<Step1FormData>({
    resolver: zodResolver(Step1Schema),
    defaultValues: {
      planName: initialData?.planName || '',
      planType: initialData?.planType || 'SINGLE',
      planAsOfDate: initialData?.planAsOfDate || new Date().toISOString().split('T')[0],
    },
  });

  // Auto-save on blur (when user leaves a field)
  const handleBlur = () => {
    if (isDirty) {
      const data = watch();
      onSave(data as Step1FormData);
    }
  };

  const onSubmit = (data: Step1FormData) => {
    onNext(data);
  };

  return (
    <div className="step-container">
      <div className="step-header">
        <div className="step-header-row">
          <h2>Let's start your retirement plan</h2>
          <TourButton tour={WIZARD_STEP1_TOUR} variant="text" />
        </div>
        <p>Tell us the basics about your retirement planning needs</p>
      </div>

      <form onSubmit={(e) => e.preventDefault()} onKeyDown={wizardFormKeyDown} className="step-form">
        {/* Plan Name */}
        <div className="form-group">
          <label htmlFor="planName" className="form-label">
            Plan Name <span className="required">*</span>
          </label>
          <input
            id="planName"
            type="text"
            className={`form-input ${errors.planName ? 'error' : ''}`}
            placeholder="e.g., Our Retirement Plan"
            {...register('planName')}
            onBlur={handleBlur}
          />
          {errors.planName && (
            <span className="error-message">{errors.planName.message}</span>
          )}
        </div>

        {/* Plan Type */}
        <fieldset className="form-group">
          <legend className="form-label">
            Plan Type <span className="required">*</span>
          </legend>
          <div className="radio-group">
            <label className="radio-option">
              <input
                type="radio"
                value="SINGLE"
                {...register('planType')}
                onBlur={handleBlur}
              />
              <span className="radio-label">
                <strong>Single person plan</strong>
                <span className="radio-description">For individual retirement planning</span>
              </span>
            </label>
            <label className="radio-option">
              <input
                type="radio"
                value="JOINT"
                {...register('planType')}
                onBlur={handleBlur}
              />
              <span className="radio-label">
                <strong>Joint plan (couples)</strong>
                <span className="radio-description">For couples planning together</span>
              </span>
            </label>
          </div>
          {errors.planType && (
            <span className="error-message">{errors.planType.message}</span>
          )}
        </fieldset>

        {/* Plan As-of Date */}
        <div className="form-group">
          <label htmlFor="planAsOfDate" className="form-label">
            Plan as of date <span className="required">*</span>
          </label>
          <input
            id="planAsOfDate"
            type="date"
            className={`form-input ${errors.planAsOfDate ? 'error' : ''}`}
            {...register('planAsOfDate')}
            onBlur={handleBlur}
          />
          {errors.planAsOfDate && (
            <span className="error-message">{errors.planAsOfDate.message}</span>
          )}
          <span className="help-text">Usually today. This is the date your current balances are entered as of.</span>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button type="button" className="btn btn-primary" onClick={() => handleSubmit(onSubmit)()}>
            Next: Add People
          </button>
          {lastSaved && (
            <span className="save-indicator">
              Saved
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

export default Step1PlanBasics;
